/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ReactNode, useRef } from "react";

import * as React from "react";
import styled, { Keyframes, keyframes } from "styled-components";
import { Transition, TransitionStatus } from "react-transition-group";
import {
  ENTERED,
  ENTERING,
  EXITED,
  EXITING,
  UNMOUNTED,
} from "react-transition-group/Transition";
import { fontFamilies } from "components/shared/Styles";

const transitionDuration = 300;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  min-height: 332px;
`;

const StateTextContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

const StateText = styled.div<{ translations: Record<string, string> }>`
  transition: opacity ${transitionDuration}ms ease-in-out;
  opacity: 0;
  margin-top: 26px;
  grid-row-start: 1;
  grid-column-start: 1;
  text-align: center;

  &::after {
    color: var(--spectrum-global-color-gray-900);
    font: normal normal normal 16px/20px ${fontFamilies};
  }
`;

const UploadingStateText = styled(StateText)`
  &::after {
    content: "${(props): string => `${props.translations.uploading}`}";
  }
`;

const progressStateMultiStepFade = (
  translations: Record<string, string>
): Keyframes => keyframes`

    0% {
      opacity: 100%;
      content: "${translations.enhancing}";
    }

    32% {
      // 10s hold
      opacity: 100%;
    }

    32.7% {
      // 10s hold + 0.2s fade out
      // fading out
      opacity: 0;
      content: "${translations.enhancing}";
    }

    33.3% {
      // 10s hold + 0.2s fade out + 0.2s pause
      opacity: 0;
      content: "${translations.still_enhancing}";
    }

    34% {
      // 10s hold + 0.2s fade out + 0.2s pause + 0.2s fade in
      // = 10.6s
      opacity: 100%;
    }

    66% {
      // 10.6s + 10s hold
      opacity: 100%;
    }

    66.7% {
      // 10.6s + 10s hold + 0.2s fade out
      opacity: 0;
      content: "${translations.still_enhancing}";
    }
    67.3% {
      // 10.6s + 10s hold + 0.2s fade out + 0.2s pause
      opacity: 0;
      content: "${translations.up_to_ten_minutes}";
    }

    67.9% {
      // 10.6s + 10s hold + 0.2s fade out + 0.2s pause + 0.2s fade in
      // = 21.2s
      opacity: 100%;
    }

    100% {
      // = 31.2s
      opacity: 100%;
      content: "${translations.up_to_ten_minutes}";
    }
`;

const ProgressStateText = styled(StateText)`
  &::after {
    content: "${(props): string => `${props.translations.enhancing}`}";
    animation: ${(props): Keyframes =>
      progressStateMultiStepFade(props.translations)};
    animation-duration: 31.2s;
    animation-fill-mode: forwards;
  }
`;

const transitionStyles: Record<TransitionStatus, React.CSSProperties> = {
  [ENTERING]: { opacity: 0 },
  [ENTERED]: { opacity: 1 },
  [EXITING]: { opacity: 0 },
  [EXITED]: { opacity: 0 },
  [UNMOUNTED]: { opacity: 0 },
};

const getStateTranslations = (): Record<string, string> => ({
  uploading: I18n.t("enhance_speech.progress.uploading"),
  enhancing: I18n.t("enhance_speech.progress.enhancing"),
  still_enhancing: I18n.t("enhance_speech.progress.still_enhancing"),
  up_to_ten_minutes: I18n.t("enhance_speech.progress.up_to_ten_minutes"),
});

const getProgressTranslations = (): Record<string, string> => ({
  loading: I18n.t("enhance_speech.premium.progress.loading"),
  still_loading: I18n.t("enhance_speech.premium.progress.still_loading"),
  longer_to_load: I18n.t("enhance_speech.premium.progress.longer_to_load"),
  take_a_while: I18n.t("enhance_speech.premium.progress.take_a_while"),
});

export const EnhancingSpinner: React.FC<{
  isUploading: boolean;
  percentUploaded?: number;
}> = ({ isUploading, percentUploaded }) => {
  const nodeRefUploading = useRef<HTMLDivElement | null>(null);
  const nodeRefProgress = useRef<HTMLDivElement | null>(null);
  const stateTranslations = getStateTranslations();

  return (
    <SpinnerContainer>
      <overlay-trigger disabled={!isUploading || undefined}>
        <sp-progress-circle
          size="l"
          indeterminate={
            !isUploading || (percentUploaded ?? 0) === 0 || undefined
          }
          progress={(percentUploaded ?? 0) * 100 || undefined}
          label={I18n.t("enhance_speech.progress.enhancing")}
          slot="trigger"
        />

        <sp-tooltip slot="hover-content">
          <span>{`${Math.floor((percentUploaded ?? 0) * 100)}%`}</span>
        </sp-tooltip>
      </overlay-trigger>
      <StateTextContainer>
        <Transition
          nodeRef={nodeRefUploading}
          in={isUploading}
          timeout={transitionDuration}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          {(state): ReactNode => (
            <UploadingStateText
              ref={nodeRefUploading}
              style={{ ...transitionStyles[state] }}
              translations={stateTranslations}
            />
          )}
        </Transition>
        <Transition
          data-testid={"transition-progress"}
          nodeRef={nodeRefProgress}
          in={!isUploading}
          timeout={{
            appear: transitionDuration,
            enter: transitionDuration + 200, // .2s pause to match the multi-step-fade animation
            exit: transitionDuration,
          }}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          {(state): ReactNode => (
            <ProgressStateText
              ref={nodeRefProgress}
              style={{ ...transitionStyles[state] }}
              translations={stateTranslations}
            />
          )}
        </Transition>
      </StateTextContainer>
    </SpinnerContainer>
  );
};

const loadingProgressMultiStepFade = (
  translations: Record<string, string>
): Keyframes => keyframes`

    32% {
      // 10s hold
      opacity: 100%;
    }

    32.7% {
      // 10s hold + 0.2s fade out
      // fading out
      opacity: 0;
      content: "${translations.loading}";
    }

    33.3% {
      // 10s hold + 0.2s fade out + 0.2s pause
      opacity: 0;
      content: "${translations.still_loading}";
    }

    34% {
      // 10s hold + 0.2s fade out + 0.2s pause + 0.2s fade in
      // = 10.6s
      opacity: 100%;
    }

    66% {
      // 10.6s + 10s hold
      opacity: 100%;
    }

    66.7% {
      // 10.6s + 10s hold + 0.2s fade out
      opacity: 0;
      content: "${translations.longer_to_load}";
    }
    67.3% {
      // 10.6s + 10s hold + 0.2s fade out + 0.2s pause
      opacity: 0;
      content: "${translations.take_a_while}";
    }

    67.9% {
      // 10.6s + 10s hold + 0.2s fade out + 0.2s pause + 0.2s fade in
      // = 21.2s
      opacity: 100%;
    }

    100% {
      // = 31.2s
      opacity: 100%;
      content: "${translations.take_a_while}";
    }
`;

const LoadingProgressState = styled.div<{
  translations: Record<string, string>;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &::after {
    content: "${(props): string => `${props.translations.loading}`}";
    animation: ${(props): Keyframes =>
      loadingProgressMultiStepFade(props.translations)};
    animation-duration: 31.2s;
    animation-fill-mode: forwards;
    display: block;
    color: var(--spectrum-global-color-gray-900);
    margin-top: 26px;
    font: normal normal normal 16px/20px ${fontFamilies};
  }
`;

export const LoadingSpinner: React.FC = () => {
  const progressTranslations = getProgressTranslations();
  return (
    <LoadingProgressState
      translations={progressTranslations}
      className="loading-spinner"
    >
      <sp-progress-circle
        size="l"
        indeterminate
        label={I18n.t("enhance_speech.premium.progress.loading")}
      />
    </LoadingProgressState>
  );
};
